import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

interface RecordPaymentModalProps {
  show: boolean;
  documentId: string;
  documentNumber: string;
  amountDue: number;
  currencyCode: string;
  onSuccess: () => void;
  onHide: () => void;
  recordPayment: (payload: { documentId: string; method: string; amount: number; date?: string; reference?: string }) => Promise<void>;
  recordPaymentLoading: boolean;
}

export function RecordPaymentModal({
  show,
  documentId,
  documentNumber,
  amountDue,
  currencyCode,
  onSuccess,
  onHide,
  recordPayment,
  recordPaymentLoading,
}: RecordPaymentModalProps) {

  const [amount, setAmount] = useState<string>(amountDue > 0 ? String(amountDue) : '');
  const [method, setMethod] = useState<string>('BANK_TRANSFER');
  const [date, setDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  const [reference, setReference] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show) {
      setAmount(amountDue > 0 ? String(amountDue) : '');
      setDate(new Date().toISOString().split('T')[0]);
      setReference('');
      setError(null);
    }
  }, [show, amountDue]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: currencyCode || 'AUD' }).format(value);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const numAmount = parseFloat(amount);
    if (!Number.isFinite(numAmount) || numAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }
    // Prevent overpayment up front (the server also rejects it) — recording
    // more than the balance due would overstate cash received (audit: high).
    if (amountDue > 0 && numAmount > amountDue + 0.005) {
      setError(`Amount exceeds the ${formatCurrency(amountDue)} balance due. Enter ${formatCurrency(amountDue)} or less.`);
      return;
    }
    try {
      await recordPayment({
        documentId,
        method,
        amount: numAmount,
        date: date || undefined,
        reference: reference.trim() || undefined,
      });
      onSuccess();
      onHide();
    } catch (err: any) {
      setError(err?.message || 'Failed to record payment');
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered fullscreen="md-down">
      <Modal.Header closeButton>
        <Modal.Title>Record payment</Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <p className="text-muted small mb-3">
            {documentNumber} — Amount due: {formatCurrency(amountDue)}
          </p>
          {error && (
            <div className="alert alert-danger py-2 mb-3">
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </div>
          )}
          <div className="mb-3">
            <label className="form-label">Amount</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max={amountDue > 0 ? amountDue : undefined}
              className="form-control"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Payment method</label>
            <select
              className="form-select"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
            >
              <option value="BANK_TRANSFER">Bank transfer</option>
              <option value="CASH">Cash</option>
              <option value="PAYPAL">PayPal</option>
              <option value="CARD">Card</option>
              <option value="OTHER">Other</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Reference (optional)</label>
            <input
              type="text"
              className="form-control"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g. Cheque number"
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-light" onClick={onHide}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={recordPaymentLoading}
          >
            {recordPaymentLoading ? <span className="spinner-border spinner-border-sm me-2" /> : null}
            Record payment
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}

export default RecordPaymentModal;
